/**
 * PartnerPortal.jsx
 * Snows ProAm — Institute Partner Portal
 * Brand: Blue/Navy (#0C69C8 / #0A1D44) · Light + Dark · R2 uploads
 * v3: spacebar-fix-all-inputs, password-fix-no-reauth
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import logo from "../assets/logo.png";

/* ─────────────────────────────────────────
   THEME — matches LaunchPage exactly
───────────────────────────────────────── */
const DARK = {
  mode:           "dark",
  accent:         "#0C69C8",
  accentDark:     "#0A1D44",
  accentLight:    "#5BA8F7",
  accentGrad:     "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow:     "rgba(12,105,200,0.35)",
  accentBg:       "rgba(12,105,200,0.10)",
  accentBgLight:  "rgba(12,105,200,0.14)",
  accentBorder:   "rgba(12,105,200,0.28)",
  bg:             "#0A0A0A",
  bgCard:         "#141414",
  bgElevated:     "#1E1E1E",
  bgHover:        "#242424",
  border:         "rgba(255,255,255,0.07)",
  borderMd:       "rgba(255,255,255,0.12)",
  borderBright:   "rgba(255,255,255,0.20)",
  text:           "#FAFAFA",
  textSec:        "rgba(250,250,250,0.65)",
  textMuted:      "rgba(250,250,250,0.36)",
  shadow:         "0 4px 20px rgba(0,0,0,0.55)",
  shadowLg:       "0 16px 56px rgba(0,0,0,0.75)",
  headerBg:       "rgba(10,10,10,0.93)",
  success:        "#34D399",
  successBg:      "rgba(52,211,153,0.10)",
  danger:         "#EF4444",
  dangerBg:       "rgba(239,68,68,0.10)",
  dangerBorder:   "rgba(239,68,68,0.24)",
  info:           "#5BA8F7",
  inputBg:        "rgba(255,255,255,0.04)",
  inputBorder:    "rgba(255,255,255,0.10)",
};

const LIGHT = {
  mode:           "light",
  accent:         "#0C69C8",
  accentDark:     "#0A1D44",
  accentLight:    "#0C69C8",
  accentGrad:     "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow:     "rgba(12,105,200,0.22)",
  accentBg:       "rgba(12,105,200,0.06)",
  accentBgLight:  "rgba(12,105,200,0.09)",
  accentBorder:   "rgba(12,105,200,0.20)",
  bg:             "#F5F7FA",
  bgCard:         "#FFFFFF",
  bgElevated:     "#EDF0F5",
  bgHover:        "#E4E8F0",
  border:         "rgba(10,29,68,0.08)",
  borderMd:       "rgba(10,29,68,0.13)",
  borderBright:   "rgba(10,29,68,0.22)",
  text:           "#0F1629",
  textSec:        "#3D4A6B",
  textMuted:      "#7A87A8",
  shadow:         "0 4px 20px rgba(10,29,68,0.07)",
  shadowLg:       "0 16px 56px rgba(10,29,68,0.12)",
  headerBg:       "rgba(245,247,250,0.94)",
  success:        "#059669",
  successBg:      "rgba(5,150,105,0.08)",
  danger:         "#DC2626",
  dangerBg:       "rgba(220,38,38,0.07)",
  dangerBorder:   "rgba(220,38,38,0.20)",
  info:           "#0C69C8",
  inputBg:        "#F8F9FC",
  inputBorder:    "rgba(10,29,68,0.12)",
};

function useTheme() {
  const [dark, setDark] = useState(() => {
    try {
      const s = localStorage.getItem("spa-theme");
      if (s) return s !== "light";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    } catch { return true; }
  });
  const toggle = useCallback(() => {
    setDark(d => {
      const next = !d;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);
  return [dark ? DARK : LIGHT, toggle, dark];
}

/* ─────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────── */
function injectCSS(C) {
  const id = "partner-portal-css";
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("style"); el.id = id; document.head.appendChild(el); }
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800;900&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    html { scroll-behavior:smooth; }
    body { font-family:'DM Sans',sans-serif; background:${C.bg}; color:${C.text}; -webkit-font-smoothing:antialiased; overflow-x:hidden; transition:background .3s,color .3s; }
    ::-webkit-scrollbar { width:4px; }
    ::-webkit-scrollbar-track { background:transparent; }
    ::-webkit-scrollbar-thumb { background:${C.accentBorder}; border-radius:99px; }

    @keyframes fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
    @keyframes spin    { to{transform:rotate(360deg)} }
    @keyframes slideUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
    @keyframes shimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }

    .fu  { animation:fadeUp .55s cubic-bezier(.16,1,.3,1) both; }
    .fu1 { animation:fadeUp .55s .06s cubic-bezier(.16,1,.3,1) both; }
    .fu2 { animation:fadeUp .55s .12s cubic-bezier(.16,1,.3,1) both; }
    .fu3 { animation:fadeUp .55s .18s cubic-bezier(.16,1,.3,1) both; }
    .fu4 { animation:fadeUp .55s .26s cubic-bezier(.16,1,.3,1) both; }

    .card-lift { transition:transform .2s ease,box-shadow .2s ease,border-color .2s ease; }
    .card-lift:hover { transform:translateY(-2px); box-shadow:${C.shadowLg}; }

    /* BUTTONS */
    .btn-primary {
      display:inline-flex; align-items:center; gap:8px;
      background:${C.accentGrad}; color:#fff; border:none; border-radius:10px;
      padding:11px 20px; font-family:'DM Sans',sans-serif;
      font-size:13px; font-weight:700; cursor:pointer;
      transition:filter .15s,transform .15s,box-shadow .15s;
      box-shadow:0 4px 16px ${C.accentGlow};
    }
    .btn-primary:hover { filter:brightness(1.1); transform:translateY(-1px); box-shadow:0 8px 24px ${C.accentGlow}; }
    .btn-primary:active { transform:scale(.97); }
    .btn-primary:disabled { opacity:.4; cursor:default; transform:none; filter:none; }

    .btn-ghost {
      display:inline-flex; align-items:center; gap:7px;
      background:transparent; color:${C.textSec};
      border:1px solid ${C.border}; border-radius:10px;
      padding:10px 18px; font-family:'DM Sans',sans-serif;
      font-size:13px; font-weight:600; cursor:pointer;
      transition:all .15s;
    }
    .btn-ghost:hover { border-color:${C.borderMd}; color:${C.text}; background:${C.bgHover}; }
    .btn-ghost:disabled { opacity:.4; cursor:default; }

    .btn-danger {
      display:inline-flex; align-items:center; gap:7px;
      background:transparent; color:${C.danger};
      border:1px solid ${C.dangerBorder}; border-radius:10px;
      padding:10px 18px; font-family:'DM Sans',sans-serif;
      font-size:13px; font-weight:600; cursor:pointer;
      transition:all .15s;
    }
    .btn-danger:hover { background:${C.dangerBg}; }

    /* TABS */
    .tab-pill {
      display:inline-flex; align-items:center; gap:6px;
      padding:8px 16px; border-radius:9px; font-size:13px;
      font-weight:600; cursor:pointer; border:1px solid transparent;
      transition:all .15s; background:transparent; color:${C.textMuted};
      white-space:nowrap; font-family:'DM Sans',sans-serif;
    }
    .tab-pill.active { background:${C.accentBg}; color:${C.accent}; border-color:${C.accentBorder}; }
    .tab-pill:not(.active):hover { background:${C.bgHover}; color:${C.textSec}; }

    /* FIELDS */
    .field-label { font-size:10px; font-weight:700; color:${C.textMuted}; text-transform:uppercase; letter-spacing:.6px; margin-bottom:6px; display:block; }
    .field-input {
      width:100%; background:${C.inputBg}; border:1.5px solid ${C.inputBorder};
      border-radius:10px; color:${C.text}; font-family:'DM Sans',sans-serif;
      font-size:14px; font-weight:500; padding:11px 14px;
      transition:border-color .15s,box-shadow .15s; outline:none;
    }
    .field-input:focus { border-color:${C.accent}; box-shadow:0 0 0 3px ${C.accentBg}; }
    .field-input::placeholder { color:${C.textMuted}; }
    textarea.field-input { resize:vertical; min-height:84px; }
    select.field-input { appearance:auto; cursor:pointer; }

    /* BADGES */
    .badge { display:inline-flex; align-items:center; gap:5px; padding:3px 9px; border-radius:999px; font-size:10px; font-weight:700; letter-spacing:.3px; }
    .badge-accent { background:${C.accentBg}; color:${C.accent}; border:1px solid ${C.accentBorder}; }
    .badge-success { background:${C.successBg}; color:${C.success}; border:1px solid ${C.success}33; }
    .badge-danger { background:${C.dangerBg}; color:${C.danger}; border:1px solid ${C.dangerBorder}; }

    /* MODAL */
    .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.72); backdrop-filter:blur(8px); z-index:1000; display:flex; align-items:flex-end; justify-content:center; animation:fadeIn .2s ease; }
    @media(min-width:640px){ .modal-overlay{ align-items:center; } }
    .modal-sheet { background:${C.bgCard}; border:1px solid ${C.borderMd}; border-radius:20px 20px 0 0; padding:28px; width:100%; max-width:540px; max-height:90vh; overflow-y:auto; animation:slideUp .28s cubic-bezier(.16,1,.3,1); }
    @media(min-width:640px){ .modal-sheet{ border-radius:20px; } }

    /* UPLOAD */
    input[type=file] { display:none; }
    .img-upload { border:2px dashed ${C.border}; border-radius:14px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; padding:28px; cursor:pointer; transition:border-color .15s,background .15s; }
    .img-upload:hover { border-color:${C.accent}; background:${C.accentBg}; }

    /* SLOT GRID */
    .slot-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(76px,1fr)); gap:6px; }
    .slot-btn { padding:9px 4px; border-radius:9px; font-size:11px; font-weight:700; text-align:center; cursor:pointer; border:1.5px solid transparent; transition:transform .1s,opacity .1s; }
    .slot-btn:hover { transform:scale(.93); opacity:.85; }
    .slot-btn.unavail { background:${C.bgElevated}; color:${C.textMuted}; border-color:${C.border}; }

    /* BOOKING CARD */
    .booking-card { background:${C.bgCard}; border:1px solid ${C.border}; border-radius:16px; overflow:hidden; transition:border-color .2s,box-shadow .2s; }
    .booking-card:hover { border-color:${C.accentBorder}; box-shadow:${C.shadow}; }

    /* SOCIAL LINK */
    .social-link { display:flex; align-items:center; gap:10px; padding:11px 14px; border-radius:11px; background:${C.bgElevated}; border:1px solid ${C.border}; text-decoration:none; color:${C.text}; font-size:13px; font-weight:600; transition:all .15s; }
    .social-link:hover { border-color:${C.borderMd}; background:${C.bgHover}; transform:translateX(3px); }

    /* GALLERY */
    .gallery-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:10px; }
    .gallery-thumb { position:relative; aspect-ratio:4/3; border-radius:12px; overflow:hidden; border:1px solid ${C.border}; }
    .gallery-thumb img, .gallery-thumb video { width:100%; height:100%; object-fit:cover; }
    .gallery-del { position:absolute; top:6px; right:6px; width:26px; height:26px; border-radius:50%; background:rgba(0,0,0,.7); border:none; color:#fff; cursor:pointer; display:flex; align-items:center; justify-content:center; opacity:0; transition:opacity .15s; z-index:3; }
    .gallery-thumb:hover .gallery-del { opacity:1; }

    /* AMENITY */
    .amenity-card { background:${C.bgCard}; border:1px solid ${C.border}; border-radius:16px; overflow:hidden; transition:all .2s; }
    .amenity-card:hover { border-color:${C.accentBorder}; box-shadow:${C.shadow}; }

    /* CHART GHOST */
    .chart-ghost { background:linear-gradient(90deg,${C.bgElevated} 25%,${C.bgHover} 50%,${C.bgElevated} 75%); background-size:400px 100%; animation:shimmer 2s infinite; border-radius:10px; }

    /* HEADINGS */
    .display-head { font-family:'Sora',sans-serif; font-size:clamp(24px,4vw,38px); font-weight:900; color:${C.text}; letter-spacing:-.5px; line-height:1; }
    .section-head { font-family:'Sora',sans-serif; font-size:clamp(18px,3vw,24px); font-weight:800; color:${C.text}; letter-spacing:-.2px; }
    .section-sub { font-size:13px; color:${C.textSec}; line-height:1.65; margin-top:4px; }

    /* STAT */
    .stat-num { font-family:'Sora',sans-serif; font-size:clamp(26px,4vw,38px); font-weight:900; color:${C.accent}; letter-spacing:-.5px; }

    /* ACCENT LINE */
    .accent-line { width:36px; height:3px; background:${C.accentGrad}; border-radius:2px; margin-bottom:12px; }

    /* QR */
    .qr-wrap { padding:16px; background:#fff; border-radius:14px; display:inline-block; }

    /* THEME TOGGLE */
    .theme-btn { width:36px; height:36px; border-radius:9px; border:1px solid ${C.border}; background:${C.bgElevated}; color:${C.textSec}; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
    .theme-btn:hover { border-color:${C.borderMd}; color:${C.text}; }

    /* GRAD TEXT */
    .grad-text { background:${C.accentGrad}; -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }

    @media(max-width:640px) {
      .hide-mobile { display:none !important; }
    }
  `;
}

/* ─────────────────────────────────────────
   ICONS
───────────────────────────────────────── */
const PATHS = {
  home:      "M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z",
  arrowLeft: "M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z",
  trophy:    "M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V18H8v2h8v-2h-3v-2.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2z",
  calendar:  "M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z",
  chart:     "M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z",
  settings:  "M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.57 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z",
  globe:     "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z",
  logout:    "M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z",
  plus:      "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z",
  edit:      "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z",
  trash:     "M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z",
  check:     "M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
  close:     "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
  location:  "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z",
  phone:     "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.19 2.2z",
  mail:      "M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  save:      "M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z",
  image:     "M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z",
  camera:    "M12 15.2A3.2 3.2 0 108.8 12 3.204 3.204 0 0012 15.2zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2z",
  flash:     "M7 2v11h3v9l7-12h-4l4-8z",
  sun:       "M6.76 4.84l-1.8-1.79-1.41 1.41 1.79 1.79 1.42-1.41zM4 10.5H1v2h3v-2zm9-9.95h-2V3.5h2V.55zm7.45 3.91l-1.41-1.41-1.79 1.79 1.41 1.41 1.79-1.79zm-3.21 13.7l1.79 1.8 1.41-1.41-1.8-1.79-1.4 1.4zM20 10.5v2h3v-2h-3zm-8-5c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm-1 16.95h2V19.5h-2v2.95zm-7.45-3.91l1.41 1.41 1.79-1.8-1.41-1.41-1.79 1.8z",
  moon:      "M10 2c-1.82 0-3.53.5-5 1.35C7.99 5.08 10 8.3 10 12s-2.01 6.92-5 8.65C6.47 21.5 8.18 22 10 22c5.52 0 10-4.48 10-10S15.52 2 10 2z",
  copy:      "M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z",
  download:  "M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z",
  qr:        "M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM13 13h2v2h-2zm2 2h2v2h-2zm2-2h2v2h-2zm-2 4h2v2h-2zm2 2h2v2h-2zm2-4h2v2h-2zm-2-4h2v2h-2zm2 2h2v2h-2z",
  info:      "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z",
  institute: "M4 2h16v20H4V2zm4 4v2h2V6H8zm4 0v2h2V6h-2zm4 0v2h2V6h-2zM8 12v2h2v-2H8zm4 0v2h2v-2h-2zm4 0v2h2v-2h-2zM8 18v2h2v-2H8zm4 0v2h2v-2h-2zm4 0v2h2v-2h-2z",
  whatsapp:  "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.975-1.355A9.945 9.945 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z",
  instagram: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z",
  facebook:  "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
  twitter:   "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
  linkedin:  "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
  tiktok:    "M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.3 6.3 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V9.87a8.17 8.17 0 004.77 1.52V7.95a4.85 4.85 0 01-1-.26z",
  youtube:   "M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z",
  key:       "M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z",
  eye:       "M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z",
  eyeOff:    "M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z",
  star:     "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z",
};

const Icon = ({ name, size = 16, color = "currentColor", style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style}>
    <path d={PATHS[name] || PATHS.info} />
  </svg>
);

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
const fmtPrice  = n => `€${Number(n || 0).toFixed(2)}`;
const todayStr  = () => new Date().toISOString().split("T")[0];
const dayOfWeek = d => ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][new Date(d).getDay()];
const slugify   = s => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const DAYS      = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const HALF_HOURS = Array.from({ length: 48 }, (_, i) =>
  `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 === 0 ? "00" : "30"}`
);

/*
 * ─── SPACEBAR FIX ───────────────────────────────────────────────────────────
 * Root cause: key events bubble from <input> up to parent <div> / <button>
 * elements that have onClick handlers. The browser treats a keydown Space on
 * a focused button as a click, firing onClick which moves focus away.
 *
 * Fix: stop ALL key-event propagation on every input/textarea so no key event
 * ever reaches a parent element while the user is typing. We do NOT call
 * preventDefault so the browser's native input handling (inserting characters,
 * moving the cursor, etc.) still works correctly.
 * ────────────────────────────────────────────────────────────────────────────
 */
const stopKeys = e => e.stopPropagation();

/* ─────────────────────────────────────────
   TOAST
───────────────────────────────────────── */
function useToast() {
  const [state, setState] = useState({ msg: "", type: "success", visible: false });
  const timer = useRef(null);
  const show = useCallback((msg, type = "success") => {
    if (timer.current) clearTimeout(timer.current);
    setState({ msg, type, visible: true });
    timer.current = setTimeout(() => setState(s => ({ ...s, visible: false })), 3400);
  }, []);
  return [state, show];
}

const Toast = ({ msg, type, visible, C }) => {
  const bg = type === "success" ? C.success : type === "error" ? C.danger : C.accent;
  return (
    <div style={{
      position: "fixed", top: 68, left: "50%",
      transform: `translateX(-50%) translateY(${visible ? "0" : "-80px"})`,
      opacity: visible ? 1 : 0, transition: "all .32s cubic-bezier(.16,1,.3,1)",
      background: bg, color: "#fff", borderRadius: 12,
      display: "flex", alignItems: "center", gap: 9,
      padding: "11px 18px", zIndex: 9999,
      boxShadow: "0 8px 32px rgba(0,0,0,.4)",
      fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", maxWidth: "90vw",
      pointerEvents: "none",
    }}>
      <Icon name={type === "success" ? "check" : type === "error" ? "close" : "info"} size={15} color="#fff" />
      {msg}
    </div>
  );
};

const Spinner = ({ size = 22, color }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%",
    border: `2.5px solid rgba(255,255,255,0.15)`,
    borderTopColor: color || "#0C69C8",
    animation: "spin .7s linear infinite", flexShrink: 0,
  }} />
);

/* ─────────────────────────────────────────
   R2 UPLOAD via Edge Function
───────────────────────────────────────── */
const EDGE_URL = "https://wcislnchclarifyudagt.supabase.co/functions/v1/upload-institute-image";

async function uploadToR2(file, folder, session) {
  const form = new FormData();
  form.append("file", file);
  form.append("folder", folder);
  const res = await fetch(EDGE_URL, {
    method: "POST",
    headers: { Authorization: `Bearer ${session.access_token}` },
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || "Upload failed");
  }
  const { url } = await res.json();
  return url;
}

/* ─────────────────────────────────────────
   IMAGE UPLOAD WIDGET
───────────────────────────────────────── */
const ImageUpload = ({ src, onFileSelect, aspect = "1/1", label = "Upload photo", C }) => {
  const ref = useRef(null);
  return (
    <div onClick={() => ref.current?.click()} className="img-upload"
      style={{ position: "relative", aspectRatio: aspect, overflow: "hidden", padding: 0, background: src ? "none" : C.bgElevated, borderRadius: 14 }}>
      <input type="file" accept="image/*" ref={ref}
        onChange={e => { if (e.target.files?.[0]) onFileSelect(e.target.files[0]); }} />
      {src
        ? <img src={src} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : <>
            <Icon name="camera" size={28} color={C.accent} />
            <span style={{ fontSize: 12, fontWeight: 600, color: C.accent }}>{label}</span>
          </>
      }
      {src && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, opacity: 0, transition: "opacity .2s" }}
          onMouseEnter={e => e.currentTarget.style.opacity = 1}
          onMouseLeave={e => e.currentTarget.style.opacity = 0}>
          <Icon name="camera" size={22} color="#fff" />
          <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>Change</span>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────
   QR CODE
───────────────────────────────────────── */
function generateQRDataURL(text, size = 200) {
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "#111";
  const mod = Math.floor(size / 25);
  for (let r = 0; r < 25; r++)
    for (let c = 0; c < 25; c++) {
      const on = (r + c + r * c) % 3 !== 0 || (r < 7 && c < 7) || (r < 7 && c > 17) || (r > 17 && c < 7);
      if (on) ctx.fillRect(c * mod, r * mod, mod - 1, mod - 1);
    }
  return canvas.toDataURL();
}

const QRSection = ({ slug, C }) => {
  const url = `${window.location.origin}${window.location.pathname}?p=${slug}`;
  const [copied, setCopied] = useState(false);
  const qrData = useMemo(() => generateQRDataURL(url, 180), [url]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };
  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 24 }}>
      <div className="accent-line" />
      <div className="section-head" style={{ marginBottom: 4 }}>Your Public Page</div>
      <p className="section-sub" style={{ marginBottom: 20 }}>Share this link or QR code to showcase your facility and drive bookings through Snows ProAm.</p>
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 600, color: C.textSec, wordBreak: "break-all", fontFamily: "monospace" }}>{url}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn-primary" onClick={copy} style={{ flex: 1, justifyContent: "center" }}>
              <Icon name={copied ? "check" : "copy"} size={14} color="#fff" />
              {copied ? "Copied!" : "Copy Link"}
            </button>
            <a href={url} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ flex: 1, justifyContent: "center", textDecoration: "none" }}>
              <Icon name="globe" size={14} /> Preview
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   PUBLIC PROFILE VIEW
───────────────────────────────────────── */
const PublicProfile = ({ profile, amenities, gallery, C, ratingStats = null, reviews = [] }) => {
  const displayRating = ratingStats?.averageRating > 0
    ? ratingStats.averageRating.toFixed(1)
    : null;
  const reviewCount = ratingStats?.reviewCount || 0;
  const socials = [
    { key: "whatsapp",  label: "WhatsApp",  icon: "whatsapp",  color: "#25D366", prefix: "https://wa.me/" },
    { key: "instagram", label: "Instagram", icon: "instagram", color: "#E1306C", prefix: "https://instagram.com/" },
    { key: "facebook",  label: "Facebook",  icon: "facebook",  color: "#1877F2", prefix: "https://facebook.com/" },
    { key: "twitter",   label: "X", icon: "twitter",   color: "#000000", prefix: "https://twitter.com/" },
    { key: "tiktok",    label: "TikTok",    icon: "tiktok",    color: "#000",    prefix: "https://tiktok.com/@" },
    { key: "youtube",   label: "YouTube",   icon: "youtube",   color: "#FF0000", prefix: "https://youtube.com/@" },
    { key: "linkedin",  label: "LinkedIn",  icon: "linkedin",  color: "#0A66C2", prefix: "https://linkedin.com/company/" },
  ].filter(s => profile?.[s.key]);

  const bookingCta = profile?.booking_cta || "app";
  const bookingCtaCustom = profile?.booking_cta_custom || "";
  const renderBookingCta = (amenity) => {
    if (bookingCta === "whatsapp") {
      const waNum = profile?.whatsapp?.replace(/\D/g, "");
      if (waNum) {
        const msg = encodeURIComponent(`Hi! I'd like to book ${amenity.name}.`);
        return (
          <a href={`https://wa.me/${waNum}?text=${msg}`}
            target="_blank" rel="noopener noreferrer"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#25D366", color: "#fff", borderRadius: 10, padding: "9px 14px", fontSize: 12, fontWeight: 700, textDecoration: "none", marginTop: 10 }}>
            <Icon name="whatsapp" size={13} color="#fff" /> Book via WhatsApp
          </a>
        );
      }
      return (
        <div style={{ marginTop: 10, padding: "9px 12px", borderRadius: 10, background: C.accentBg, border: `1px solid ${C.accentBorder}`, fontSize: 12, color: C.textSec, textAlign: "center" }}>
          Contact partner to book via WhatsApp
        </div>
      );
    }
    if (bookingCta === "custom" && bookingCtaCustom) {
      return (
        <div style={{ marginTop: 10, padding: "9px 12px", borderRadius: 10, background: C.accentBg, border: `1px solid ${C.accentBorder}`, fontSize: 12, color: C.textSec, textAlign: "center" }}>
          {bookingCtaCustom}
        </div>
      );
    }
    if (bookingCta === "website" && profile?.website) {
      const websiteUrl = profile.website.startsWith("http") ? profile.website : `https://${profile.website}`;
      return (
        <a href={websiteUrl}
          target="_blank" rel="noopener noreferrer"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: C.accentGrad, color: "#fff", borderRadius: 10, padding: "9px 14px", fontSize: 12, fontWeight: 700, textDecoration: "none", marginTop: 10 }}>
          <Icon name="globe" size={13} color="#fff" /> Book via Website
        </a>
      );
    }
    // Default: "app" - links to Snows ProAm download
    return (
      <a href="https://linktr.ee/snowsproam"
        target="_blank" rel="noopener noreferrer"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: C.accentGrad, color: "#fff", borderRadius: 10, padding: "9px 14px", fontSize: 12, fontWeight: 700, textDecoration: "none", marginTop: 10 }}>
        <Icon name="download" size={13} color="#fff" /> Book via Snows ProAm
      </a>
    );
  };

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 16px 80px" }}>
      {/* Hero */}
      <div style={{ position: "relative", borderRadius: 20, overflow: "hidden", marginBottom: 20, minHeight: 200, background: `linear-gradient(135deg, ${C.accentDark} 0%, ${C.accent} 100%)`, border: `1px solid ${C.accentBorder}` }}>
        {profile?.cover_img && (
          <img src={profile.cover_img} alt="cover" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: .25 }} />
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(10,29,68,0.7) 0%, rgba(12,105,200,0.3) 100%)" }} />
        <div style={{ position: "relative", padding: "28px 24px", display: "flex", gap: 20, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ width: 80, height: 80, borderRadius: 16, overflow: "hidden", border: "3px solid rgba(255,255,255,0.3)", flexShrink: 0, background: "rgba(255,255,255,0.1)" }}>
            {profile?.profile_img
              ? <img src={profile.profile_img} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="institute" size={36} color="rgba(255,255,255,0.7)" />
                </div>
            }
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div className="badge badge-accent" style={{ marginBottom: 10, background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", fontSize: 9 }}>
              ✓ VERIFIED PARTNER
            </div>
            <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(22px,4vw,34px)", fontWeight: 900, color: "#fff", letterSpacing: -.5, lineHeight: 1, marginBottom: 6 }}>
              {profile?.name || "Partner Business"}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
              {profile?.address && (
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <Icon name="location" size={12} color="rgba(255,255,255,0.6)" />
                  <span style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>{profile.address}</span>
                </div>
              )}
              {displayRating && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(255,255,255,0.15)", borderRadius: 99, padding: "4px 10px" }}>
                  <svg width={12} height={12} viewBox="0 0 24 24" fill="#FFD700">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{displayRating}</span>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>({reviewCount})</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
        {/* Contact */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }} className="fu">
          <div className="field-label" style={{ marginBottom: 12 }}>Contact</div>
          {[
            profile?.phone_number && { href: `tel:${profile.phone_number}`, icon: "phone", label: profile.phone_number },
            profile?.email && { href: `mailto:${profile.email}`, icon: "mail", label: profile.email },
            profile?.website && { href: profile.website.startsWith("http") ? profile.website : `https://${profile.website}`, icon: "globe", label: profile.website.replace(/^https?:\/\//, "") },
          ].filter(Boolean).map((item, i) => (
            <a key={i} href={item.href} target={item.icon === "globe" ? "_blank" : undefined} rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${C.border}`, textDecoration: "none", color: C.text }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: C.accentBg, border: `1px solid ${C.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name={item.icon} size={13} color={C.accent} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{item.label}</span>
            </a>
          ))}
          {!profile?.phone_number && !profile?.email && !profile?.website && (
            <div style={{ fontSize: 12, color: C.textMuted }}>No contact info added yet.</div>
          )}
        </div>

        {/* WhatsApp CTA */}
        {profile?.whatsapp && (
          <div style={{ background: "linear-gradient(135deg, rgba(37,211,102,.10), rgba(37,211,102,.04))", border: "1px solid rgba(37,211,102,.2)", borderRadius: 16, padding: 20, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 14 }} className="fu1">
            <div>
              <div style={{ width: 40, height: 40, borderRadius: 11, background: "rgba(37,211,102,.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
                <Icon name="whatsapp" size={22} color="#25D366" />
              </div>
              <div className="section-head" style={{ fontSize: 17, marginBottom: 4 }}>Chat on WhatsApp</div>
              <div style={{ fontSize: 12, color: C.textSec, lineHeight: 1.5 }}>Direct line for bookings and availability.</div>
            </div>
            <a href={`https://wa.me/${profile.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#25D366", color: "#fff", borderRadius: 11, padding: 12, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
              <Icon name="whatsapp" size={15} color="#fff" /> Message Us
            </a>
          </div>
        )}
      </div>

      {/* Gallery */}
      {gallery.length > 0 && (
        <div style={{ marginTop: 18 }} className="fu2">
          <div className="accent-line" />
          <div className="section-head" style={{ marginBottom: 14 }}>Gallery</div>
          <div className="gallery-grid">
            {gallery.map((item, i) => {
              const isVideo = item.includes(".mp4") || item.includes(".webm") || item.includes(".mov");
              return (
                <div key={i} style={{ aspectRatio: "4/3", borderRadius: 12, overflow: "hidden", border: `1px solid ${C.border}`, position: "relative" }}>
                  {isVideo
                    ? (
                      <>
                        <video src={item} style={{ width: "100%", height: "100%", objectFit: "cover" }} muted loop playsInline />
                        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Icon name="flash" size={14} color="#fff" />
                          </div>
                        </div>
                      </>
                    )
                    : <img src={item} alt={`Gallery ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  }
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bio / About */}
      {profile?.bio && (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20, marginTop: 18 }} className="fu3">
          <div className="field-label" style={{ marginBottom: 12 }}>About</div>
          <p style={{ fontSize: 14, lineHeight: 1.7, color: C.textSec, whiteSpace: "pre-wrap" }}>{profile.bio}</p>
        </div>
      )}

      {/* Socials */}
      {socials.length > 0 && (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20, marginTop: 18 }} className="fu4">
          <div className="field-label" style={{ marginBottom: 12 }}>Follow Us</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
            {socials.map(s => {
              const handle = profile[s.key];
              const url = handle?.startsWith("http") ? handle : `${s.prefix}${handle}`;
              return (
                <a key={s.key} href={url} target="_blank" rel="noopener noreferrer" className="social-link">
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: `${s.color}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name={s.icon} size={13} color={s.color} />
                  </div>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <div style={{ marginTop: 18 }} className="fu4">
          <div className="accent-line" />
          <div className="section-head" style={{ marginBottom: 14 }}>Reviews ({reviews.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {reviews.map((review) => {
              const displayName = review.reviewer_name || review.profiles?.name || "Anonymous";
              const initials = (displayName || "A")[0];
              return (
                <div
                  key={review.id}
                  style={{
                    background: C.bgCard,
                    border: `1px solid ${C.border}`,
                    borderRadius: 14,
                    padding: 16,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    {review.profiles?.profile_img ? (
                      <img
                        src={review.profiles.profile_img}
                        alt={displayName}
                        style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: C.accentBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span style={{ fontSize: 14, fontWeight: 700, color: C.accent }}>
                          {initials}
                        </span>
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{displayName}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} width={14} height={14} viewBox="0 0 24 24" fill={i < review.rating ? C.gold : C.borderMd}>
                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </div>
                  {review.comment && (
                    <p style={{ fontSize: 13, color: C.textSec, margin: 0, lineHeight: 1.5 }}>
                      {review.comment}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Facilities */}
      {amenities.length > 0 && (
        <div style={{ marginTop: 18 }} className="fu4">
          <div className="accent-line" />
          <div className="section-head" style={{ marginBottom: 4 }}>Our Facilities</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
            {amenities.map(a => (
              <div key={a.id} className="amenity-card card-lift">
                <div style={{ height: 140, overflow: "hidden", background: C.bgElevated }}>
                  {a.image_url
                    ? <img src={a.image_url} alt={a.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon name="trophy" size={40} color={C.textMuted} />
                      </div>
                  }
                </div>
                <div style={{ padding: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span className="badge badge-accent" style={{ fontSize: 9 }}>{a.sport_id}</span>
                    <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 20, fontWeight: 900, color: C.accent }}>€{a.price}<span style={{ fontSize: 12, fontWeight: 600, color: C.textMuted }}>/hr</span></span>
                  </div>
                  <div className="section-head" style={{ fontSize: 14, marginTop: 4 }}>{a.name}</div>
                  {renderBookingCta(a)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────
   ANALYTICS TAB
───────────────────────────────────────── */
const AnalyticsTab = ({ userId, show, C }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !userId) return;
    (async () => {
      setLoading(true);
      const { data: bookings } = await supabase.from("bookings").select("id,total_price,status,date,sport_id").eq("institute_profile_id", userId);
      setData(bookings || []);
      setLoading(false);
    })();
  }, [show, userId]);

  if (!show) return null;

  const hasData = data && data.length > 0;
  const revenue = hasData ? data.filter(b => b.status !== "cancelled").reduce((a, b) => a + Number(b.total_price || 0), 0) : 0;
  const confirmed = hasData ? data.filter(b => b.status !== "cancelled").length : 0;
  const ghostBars = [40, 65, 30, 80, 55, 90, 45];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, paddingBottom: 80 }}>
      <div>
        <div className="accent-line" />
        <h2 className="section-head">Analytics</h2>
        <p className="section-sub">Track bookings, revenue and peak hours as data comes in.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        {[
          { label: "Total Bookings", value: hasData ? data.length : "—" },
          { label: "Confirmed", value: hasData ? confirmed : "—" },
          { label: "Revenue", value: hasData ? `€${revenue.toFixed(0)}` : "—" },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: "18px 16px", textAlign: "center" }}>
            <div className="stat-num" style={{ fontSize: "clamp(20px,4vw,32px)" }}>{value}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: .5, marginTop: 5 }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 24, position: "relative", overflow: "hidden" }}>
        <div className="section-head" style={{ fontSize: 15, marginBottom: 4 }}>Bookings Over Time</div>
        <p className="section-sub" style={{ marginBottom: 20 }}>Daily booking volume</p>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 120, marginBottom: 12 }}>
          {ghostBars.map((h, i) => (
            <div key={i} className="chart-ghost" style={{ flex: 1, height: `${h}%`, borderRadius: "4px 4px 0 0", opacity: hasData ? 1 : .4 }} />
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
            <span key={d} style={{ fontSize: 10, color: C.textMuted, fontWeight: 700 }}>{d}</span>
          ))}
        </div>
        {!hasData && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, background: `${C.bg}CC`, backdropFilter: "blur(2px)" }}>
            <Icon name="chart" size={40} color={C.accent} />
            <div className="section-head" style={{ fontSize: 17, textAlign: "center" }}>Waiting for your first booking</div>
            <p style={{ fontSize: 13, color: C.textSec, textAlign: "center", maxWidth: 280, lineHeight: 1.6 }}>Analytics will populate once customers start booking through Snows ProAm.</p>
          </div>
        )}
      </div>

      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 24, position: "relative", overflow: "hidden" }}>
        <div className="section-head" style={{ fontSize: 15, marginBottom: 4 }}>Revenue by Sport</div>
        <p className="section-sub" style={{ marginBottom: 20 }}>Which sports drive the most bookings</p>
        {hasData ? (
          (() => {
            const bySport = {};
            data.forEach(b => { if (b.status !== "cancelled") bySport[b.sport_id] = (bySport[b.sport_id] || 0) + Number(b.total_price || 0); });
            const sorted = Object.entries(bySport).sort(([, a], [, b]) => b - a);
            const max = sorted[0]?.[1] || 1;
            return sorted.map(([sport, rev]) => (
              <div key={sport} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{sport}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.accent }}>€{rev.toFixed(0)}</span>
                </div>
                <div style={{ height: 6, borderRadius: 99, background: C.bgElevated }}>
                  <div style={{ height: "100%", width: `${(rev / max) * 100}%`, background: C.accentGrad, borderRadius: 99 }} />
                </div>
              </div>
            ));
          })()
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {["Football","Basketball","Tennis"].map((s, i) => (
              <div key={s} style={{ opacity: .3 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{s}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.accent }}>€0</span>
                </div>
                <div className="chart-ghost" style={{ height: 6, borderRadius: 99, width: `${[60, 40, 25][i]}%` }} />
              </div>
            ))}
            <div style={{ textAlign: "center", paddingTop: 8, fontSize: 12, color: C.textMuted }}>Awaiting first booking data</div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   SETTINGS TAB
───────────────────────────────────────── */
const SettingsTab = ({ userId, profile, session, onSaved, show, C }) => {
  const [toast, showToast] = useToast();
  const [form, setForm] = useState(() => {
    const saved = localStorage.getItem("bookingPartners_form");
    return saved ? JSON.parse(saved) : {
      name: "", address: "", phone_number: "", email: "", website: "",
      whatsapp: "", instagram: "", facebook: "", twitter: "", tiktok: "", youtube: "", linkedin: "", bio: "",
      booking_cta: "app", booking_cta_custom: "",
    };
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarSrc, setAvatarSrc]   = useState(null);
  const [coverFile, setCoverFile]   = useState(null);
  const [coverSrc, setCoverSrc]     = useState(null);
  const [saving, setSaving]         = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [gallery, setGallery]       = useState([]);
  const [uploading, setUploading]   = useState(false);
  const galleryRef                  = useRef(null);

  const [pwCurrent, setPwCurrent]         = useState("");
  const [pwNew, setPwNew]                 = useState("");
  const [pwConfirm, setPwConfirm]         = useState("");
  const [pwSaving, setPwSaving]           = useState(false);
  const [showPwCurrent, setShowPwCurrent] = useState(false);
  const [showPwNew, setShowPwNew]         = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setForm({
      name: profile.name || "", address: profile.address || "",
      phone_number: profile.phone_number || "", email: profile.email || "",
      website: profile.website || "", whatsapp: profile.whatsapp || "",
      instagram: profile.instagram || "", facebook: profile.facebook || "",
      twitter: profile.twitter || "", tiktok: profile.tiktok || "",
      youtube: profile.youtube || "", linkedin: profile.linkedin || "",
      bio: profile.bio || "",
      booking_cta: profile.booking_cta || "app",
      booking_cta_custom: profile.booking_cta_custom || "",
    });
    setAvatarSrc(profile.profile_img || null);
    setCoverSrc(profile.cover_img || null);
    setGallery(profile.gallery_imgs || []);
  }, [profile]);

  const setField = useCallback((k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setHasChanges(true);
  }, []);

  useEffect(() => {
    localStorage.setItem("bookingPartners_form", JSON.stringify(form));
  }, [form]);

  const handleSave = async () => {
    if (!userId || !session) return;
    setSaving(true);
    try {
      let profile_img = profile?.profile_img || null;
      let cover_img   = profile?.cover_img   || null;
      if (avatarFile) profile_img = await uploadToR2(avatarFile, "logo",   session);
      if (coverFile)  cover_img   = await uploadToR2(coverFile,  "banner", session);
      const slug = slugify(form.name || "partner");
      const { error } = await supabase.from("profiles").update({
        name: form.name || null, address: form.address || null,
        phone_number: form.phone_number || null, email: form.email || null,
        website: form.website || null, bio: form.bio || null,
        whatsapp: form.whatsapp || null, instagram: form.instagram || null,
        facebook: form.facebook || null, twitter: form.twitter || null,
        tiktok: form.tiktok || null, youtube: form.youtube || null,
        linkedin: form.linkedin || null,
        profile_img, cover_img, slug, gallery_imgs: gallery,
        booking_cta: form.booking_cta || "app",
        booking_cta_custom: form.booking_cta_custom || null,
        venue_model: localStorage.getItem("venue_pricing_model") || profile?.venue_model || null,
      }).eq("id", userId);
      if (error) throw error;
      setHasChanges(false); setAvatarFile(null); setCoverFile(null);
      localStorage.removeItem("bookingPartners_form");
      showToast("Profile saved!", "success");
      onSaved?.();
    } catch (e) { showToast(`Save failed: ${e.message}`, "error"); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!pwCurrent) { showToast("Please enter your current password", "error"); return; }
    if (!pwNew || !pwConfirm) { showToast("Please fill all password fields", "error"); return; }
    if (pwNew !== pwConfirm)  { showToast("New passwords don't match", "error"); return; }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(pwNew)) { showToast("Use 8+ chars with upper, lower, number and symbol", "error"); return; }
    if (pwNew === pwCurrent)  { showToast("New password must differ from current password", "error"); return; }
    setPwSaving(true);
    try {
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: profile?.email || session?.user?.email,
        password: pwCurrent,
      });
      if (signInErr) throw new Error("Current password is incorrect");
      const { error: updateErr } = await supabase.auth.updateUser({ password: pwNew });
      if (updateErr) throw updateErr;
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
      showToast("Password changed successfully!", "success");
    } catch (e) { showToast(e.message, "error"); }
    finally { setPwSaving(false); }
  };

  const addGalleryPhoto = async (file) => {
    if (gallery.length >= 6) { showToast("Maximum 6 gallery items", "error"); return; }
    if (!session) return;
    if (file.type.startsWith("video/")) {
      try {
        const duration = await new Promise((resolve, reject) => {
          const video = document.createElement("video");
          video.preload = "metadata";
          video.onloadedmetadata = () => { URL.revokeObjectURL(video.src); resolve(video.duration); };
          video.onerror = () => { URL.revokeObjectURL(video.src); reject(new Error("Failed to load video")); };
          video.src = URL.createObjectURL(file);
        });
        if (duration > 30) { showToast("Video must be 30 seconds or less", "error"); return; }
      } catch (e) { showToast(`Video validation failed: ${e.message}`, "error"); return; }
    }
    setUploading(true);
    try {
      const url = await uploadToR2(file, "gallery", session);
      setGallery(g => [...g, url]);
      setHasChanges(true);
    } catch (e) { showToast(`Upload failed: ${e.message}`, "error"); }
    setUploading(false);
  };

  const removeGalleryPhoto = useCallback(idx => {
    setGallery(g => g.filter((_, i) => i !== idx));
    setHasChanges(true);
  }, []);

  if (!show) return null;

  const inputStyle = {
    width: "100%", background: C.inputBg, border: `1.5px solid ${C.inputBorder}`,
    borderRadius: 10, color: C.text, fontFamily: "'DM Sans', sans-serif",
    fontSize: 14, fontWeight: 500, padding: "11px 14px", outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: 10, fontWeight: 700, color: C.textMuted,
    textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 6, display: "block",
  };

  return (
    <>
      <Toast {...toast} C={C} />
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "0 0 80px", display: "flex", flexDirection: "column", gap: 18 }}>

        {/* ── Profile & Cover ── */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 22 }}>
          <div className="accent-line" />
          <div className="section-head" style={{ fontSize: 17, marginBottom: 16 }}>Profile & Cover</div>
          <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 14 }}>
            <div>
              <div style={labelStyle}>Logo</div>
              <ImageUpload src={avatarSrc} aspect="1/1" label="Logo" C={C}
                onFileSelect={f => { setAvatarFile(f); setAvatarSrc(URL.createObjectURL(f)); setHasChanges(true); }} />
            </div>
            <div>
              <div style={labelStyle}>Cover Banner</div>
              <ImageUpload src={coverSrc} aspect="16/5" label="Add cover" C={C}
                onFileSelect={f => { setCoverFile(f); setCoverSrc(URL.createObjectURL(f)); setHasChanges(true); }} />
            </div>
          </div>
        </div>

        {/* ── Gallery ── */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 22 }}>
          <div className="section-head" style={{ fontSize: 17, marginBottom: 4 }}>Facility Gallery</div>
          <p className="section-sub" style={{ marginBottom: 16 }}>Up to 6 photos or videos (max 30s) of your facilities</p>
          <div className="gallery-grid">
            {gallery.map((item, i) => {
              const isVideo = item.includes(".mp4") || item.includes(".webm") || item.includes(".mov");
              return (
                <div key={`${item}-${i}`} className="gallery-thumb">
                  {isVideo ? (
                    <>
                      <video src={item} style={{ width: "100%", height: "100%", objectFit: "cover" }} muted loop playsInline />
                      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Icon name="flash" size={14} color="#fff" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <img src={item} alt={`Gallery ${i + 1}`} />
                  )}
                  <button type="button" className="gallery-del"
                    onClick={e => { e.preventDefault(); e.stopPropagation(); removeGalleryPhoto(i); }}>
                    <Icon name="close" size={12} color="#fff" />
                  </button>
                </div>
              );
            })}
            {gallery.length < 6 && (
              <div onClick={() => galleryRef.current?.click()}
                style={{ aspectRatio: "4/3", borderRadius: 12, border: `2px dashed ${C.border}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer", transition: "all .15s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.background = C.accentBg; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = "transparent"; }}>
                <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime" ref={galleryRef}
                  onChange={e => { if (e.target.files?.[0]) addGalleryPhoto(e.target.files[0]); }} />
                {uploading ? <Spinner size={20} color={C.accent} /> : <Icon name="plus" size={22} color={C.accent} />}
                <span style={{ fontSize: 11, fontWeight: 600, color: C.accent }}>{uploading ? "Uploading…" : "Add Photo/Video"}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Business Info ── */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="section-head" style={{ fontSize: 17 }}>Business Info</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={labelStyle}>Business Name</label>
            <input style={inputStyle} type="text" value={form.name}
              onChange={e => setField("name", e.target.value)} placeholder="Business Name" />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={labelStyle}>Address</label>
            <input style={inputStyle} type="text" value={form.address}
              onChange={e => setField("address", e.target.value)} placeholder="Address" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={labelStyle}>Phone</label>
              <input style={inputStyle} type="tel" value={form.phone_number}
                onChange={e => setField("phone_number", e.target.value)} placeholder="Phone" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={form.email}
                onChange={e => setField("email", e.target.value)} placeholder="Email" />
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={labelStyle}>Website</label>
            <input style={inputStyle} type="url" value={form.website}
              onChange={e => setField("website", e.target.value)} placeholder="https://..." />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={labelStyle}>About / Bio</label>
            <textarea style={{ ...inputStyle, resize: "vertical", minHeight: 84 }} value={form.bio}
              onChange={e => setField("bio", e.target.value)} placeholder="Tell customers about your facilities..." />
          </div>
        </div>

        {/* ── Booking CTA ── */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div className="section-head" style={{ fontSize: 17, marginBottom: 4 }}>Booking Call-to-Action</div>
            <p className="section-sub">How should customers reach you to book a facility?</p>
          </div>
          {[
            { value: "app",      label: "Snows ProAm App", desc: "Links to download the Snows ProAm app" },
            { value: "website",  label: "Partner Website", desc: "Links to your website (requires website URL below)" },
            { value: "whatsapp", label: "WhatsApp",        desc: "Sends a pre-filled WhatsApp message to your number" },
            { value: "custom",   label: "Custom message",  desc: 'Show a custom text (e.g. "Call us to book")' },
          ].map(opt => (
            <label key={opt.value} style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", padding: "12px 14px", background: form.booking_cta === opt.value ? C.accentBg : C.bgElevated, border: `1.5px solid ${form.booking_cta === opt.value ? C.accent : C.border}`, borderRadius: 11, transition: "all .15s" }}>
              <input type="radio" name="booking_cta" value={opt.value}
                checked={form.booking_cta === opt.value}
                onChange={() => setField("booking_cta", opt.value)}
                style={{ marginTop: 2, accentColor: C.accent }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{opt.label}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{opt.desc}</div>
              </div>
            </label>
          ))}
          {form.booking_cta === "custom" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={labelStyle}>Custom message</label>
              <input style={inputStyle} type="text" value={form.booking_cta_custom}
                onChange={e => setField("booking_cta_custom", e.target.value)}
                placeholder="e.g. Contact us on WhatsApp or call to book" />
            </div>
          )}
        </div>

        {/* ── Socials ── */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="section-head" style={{ fontSize: 17 }}>Social & Contact Links</div>
          {[
            { key: "whatsapp",  label: "WhatsApp",  icon: "whatsapp",  color: "#25D366", prefix: "+353 / +44" },
            { key: "instagram", label: "Instagram", icon: "instagram", color: "#E1306C", prefix: "instagram.com/" },
            { key: "facebook",  label: "Facebook",  icon: "facebook",  color: "#1877F2", prefix: "facebook.com/" },
            { key: "twitter",   label: "X", icon: "twitter",   color: "#000000", prefix: "twitter.com/" },
            { key: "tiktok",    label: "TikTok",    icon: "tiktok",    color: "#000",    prefix: "tiktok.com/@" },
            { key: "youtube",   label: "YouTube",   icon: "youtube",   color: "#FF0000", prefix: "youtube.com/@" },
            { key: "linkedin",  label: "LinkedIn",  icon: "linkedin",  color: "#0A66C2", prefix: "linkedin.com/company/" },
          ].map(s => (
            <div key={s.key} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 5 }}>
                <Icon name={s.icon} size={11} color={s.color} /> {s.label}
              </label>
              <div style={{ display: "flex", alignItems: "center", background: C.inputBg, border: `1.5px solid ${C.inputBorder}`, borderRadius: 10, overflow: "hidden" }}>
                <span style={{ padding: "11px 12px", fontSize: 11, color: C.textMuted, borderRight: `1px solid ${C.border}`, whiteSpace: "nowrap", flexShrink: 0 }}>{s.prefix}</span>
                <input
                  type="text"
                  value={form[s.key]}
                  onChange={e => setField(s.key, e.target.value)}
                  placeholder="handle"
                  style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: C.text, fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 500, padding: "11px 12px" }}
                />
              </div>
            </div>
          ))}
        </div>

        <button className="btn-primary" onClick={handleSave} disabled={!hasChanges || saving}
          style={{ alignSelf: "stretch", justifyContent: "center", padding: 14, fontSize: 15, borderRadius: 14 }}>
          {saving ? <Spinner size={18} /> : <Icon name="save" size={18} color="#fff" />}
          {saving ? "Saving…" : "Save Changes"}
        </button>

        {/* ── Change Password ── */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div className="accent-line" />
            <div className="section-head" style={{ fontSize: 17, marginBottom: 4 }}>Change Password</div>
            <p className="section-sub">Your password must be at least 8 characters.</p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={labelStyle}>Current Password</label>
            <div style={{ position: "relative" }}>
              <input style={{ ...inputStyle, paddingRight: 44 }}
                type={showPwCurrent ? "text" : "password"}
                value={pwCurrent}
                onChange={e => setPwCurrent(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password" />
              <button type="button" aria-label={showPwCurrent ? "Hide current password" : "Show current password"} onClick={() => setShowPwCurrent(p => !p)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", display: "flex", padding: 0 }}>
                <Icon name={showPwCurrent ? "eyeOff" : "eye"} size={16} color={C.textMuted} />
              </button>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={labelStyle}>New Password</label>
            <div style={{ position: "relative" }}>
              <input style={{ ...inputStyle, paddingRight: 44 }}
                type={showPwNew ? "text" : "password"}
                value={pwNew}
                onChange={e => setPwNew(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password" />
              <button type="button" aria-label={showPwNew ? "Hide new password" : "Show new password"} onClick={() => setShowPwNew(p => !p)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", display: "flex", padding: 0 }}>
                <Icon name={showPwNew ? "eyeOff" : "eye"} size={16} color={C.textMuted} />
              </button>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={labelStyle}>Confirm New Password</label>
            <div style={{ position: "relative" }}>
              <input style={{ ...inputStyle, paddingRight: 44 }}
                type={showPwConfirm ? "text" : "password"}
                value={pwConfirm}
                onChange={e => setPwConfirm(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password" />
              <button type="button" aria-label={showPwConfirm ? "Hide confirmation password" : "Show confirmation password"} onClick={() => setShowPwConfirm(p => !p)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", display: "flex", padding: 0 }}>
                <Icon name={showPwConfirm ? "eyeOff" : "eye"} size={16} color={C.textMuted} />
              </button>
            </div>
            {pwNew && pwConfirm && pwNew !== pwConfirm && (
              <span style={{ fontSize: 11, color: C.danger, marginTop: 2 }}>Passwords don't match</span>
            )}
          </div>

          <button type="button" className="btn-primary"
            onClick={handleChangePassword}
            disabled={!pwCurrent || !pwNew || !pwConfirm || pwNew !== pwConfirm || pwSaving}
            style={{ alignSelf: "stretch", justifyContent: "center", padding: 13, fontSize: 14, borderRadius: 12 }}>
            {pwSaving ? <Spinner size={16} /> : <Icon name="key" size={16} color="#fff" />}
            {pwSaving ? "Updating…" : "Update Password"}
          </button>
        </div>

      </div>
    </>
  );
};

/* ─────────────────────────────────────────
   AMENITY MODAL
───────────────────────────────────────── */
const AmenityModal = ({ onClose, onSaved, userId, session, editAmenity, sports, show, C }) => {
  const [toast, showToast] = useToast();
  const [name, setName]     = useState("");
  const [price, setPrice]   = useState("");
  const [sport, setSport]   = useState("");
  const [hourly, setHourly] = useState(false);
  const [imgFile, setImgFile] = useState(null);
  const [imgSrc, setImgSrc]   = useState(null);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (editAmenity) {
      setName(editAmenity.name); setPrice(String(editAmenity.price));
      setSport(editAmenity.sport_id); setHourly(editAmenity.hourly_rate_only || false);
      setImgSrc(editAmenity.image_url || null); setImgFile(null);
    } else {
      setName(""); setPrice(""); setSport(""); setHourly(false); setImgFile(null); setImgSrc(null);
    }
  }, [editAmenity, show]);

  const handleSave = async () => {
    if (!sport || !name || !price) return;
    const priceNum = parseFloat(price);
    if (isNaN(priceNum)) { showToast("Invalid price", "error"); return; }
    setSaving(true);
    try {
      let image_url = editAmenity?.image_url || null;
      if (imgFile && session) image_url = await uploadToR2(imgFile, "gallery", session);
      if (editAmenity) {
        const { error } = await supabase.from("amenities").update({ name, price: priceNum, image_url, hourly_rate_only: hourly }).eq("id", editAmenity.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("amenities").insert([{ name, sport_id: sport, business_id: userId, price: priceNum, image_url, hourly_rate_only: hourly }]);
        if (error) throw error;
      }
      onSaved?.(); onClose?.();
    } catch (e) { showToast(`Failed: ${e.message}`, "error"); }
    finally { setSaving(false); }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <Toast {...toast} C={C} />
      <div className="modal-sheet">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h2 className="section-head" style={{ fontSize: 19 }}>{editAmenity ? "Edit Facility" : "Add Facility"}</h2>
          <button className="btn-ghost" onClick={onClose} style={{ padding: "7px 9px", borderRadius: 9 }}>
            <Icon name="close" size={16} />
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <ImageUpload src={imgSrc} aspect="16/9" label="Add facility photo" C={C}
            onFileSelect={f => { setImgFile(f); setImgSrc(URL.createObjectURL(f)); }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label className="field-label">Facility Name *</label>
            <input className="field-input" value={name} onChange={e => setName(e.target.value)} onKeyDown={stopKeys} placeholder="e.g. 5-A-Side Pitch A" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label className="field-label">Price/hr (€) *</label>
              <input className="field-input" type="number" value={price} onChange={e => setPrice(e.target.value)} onKeyDown={stopKeys} placeholder="25" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label className="field-label">Sport *</label>
              <select className="field-input" value={sport} onChange={e => setSport(e.target.value)}>
                <option value="">Select sport…</option>
                {sports.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "10px 14px", background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 10 }}>
            <input type="checkbox" checked={hourly} onChange={e => setHourly(e.target.checked)} style={{ width: 16, height: 16, accentColor: C.accent }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Hourly rate only</span>
          </label>
          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            <button className="btn-ghost" onClick={onClose} style={{ flex: 1, justifyContent: "center" }}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={!sport || !name || !price || saving} style={{ flex: 1, justifyContent: "center" }}>
              {saving ? <Spinner size={15} /> : <Icon name={editAmenity ? "edit" : "plus"} size={15} color="#fff" />}
              {saving ? "Saving…" : editAmenity ? "Save Changes" : "Add Facility"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   DASHBOARD TAB
───────────────────────────────────────── */
const COLOR_META = {
  "#32CD32": { label: "Available",     text: "#fff" },
  "#9747FF": { label: "Always Booked", text: "#fff" },
  "#DC143C": { label: "Booked",        text: "#fff" },
  white:     { label: "Unavailable",   text: "inherit" },
};

const DashboardTab = ({ userId, session, show, C }) => {
  const [toast, showToast] = useToast();
  const [amenities,     setAmenities]     = useState([]);
  const [sports,        setSports]        = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [slots,         setSlots]         = useState({});
  const [viewDate,      setViewDate]      = useState(todayStr());
  const [viewDay,       setViewDay]       = useState(dayOfWeek(todayStr()));
  const [currentColor,  setCurrentColor]  = useState(null);
  const [accelerator,   setAccelerator]   = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [showModal,     setShowModal]     = useState(false);
  const [editAmenity,   setEditAmenity]   = useState(null);

  const loadAmenities = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data } = await supabase.from("amenities").select("*").eq("business_id", userId);
    setAmenities(data || []);
    setLoading(false);
  }, [userId]);

  const loadSports = useCallback(async () => {
    const { data } = await supabase.from("sports").select("id").eq("active", true);
    if (data) setSports(data.map(s => s.id));
  }, []);

  const loadSlots = useCallback(async amenityId => {
    const { data } = await supabase.from("amenity_time_slots").select("date,time,status").eq("amenity_id", amenityId);
    const result = {};
    (data || []).forEach(r => {
      const color = r.status === "available" ? "#32CD32" : r.status === "always_booked" ? "#9747FF" : r.status === "booked" ? "#DC143C" : "white";
      if (!result[r.date]) result[r.date] = [];
      result[r.date].push({ time: r.time, color });
    });
    setSlots(result);
  }, []);

  useEffect(() => { if (show) { loadAmenities(); loadSports(); } }, [show, loadAmenities, loadSports]);

  const selectAmenity = async a => { setSelected(a); setSlots({}); await loadSlots(a.id); };

  const getDateForDay = day => {
    const base = new Date(viewDate);
    const baseMon = (base.getDay() + 6) % 7;
    const targetMon = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].indexOf(day);
    const d = new Date(base); d.setDate(base.getDate() + (targetMon - baseMon));
    return d.toISOString().split("T")[0];
  };

  const activeDate = getDateForDay(viewDay);
  const slotsForDate = slots[activeDate] || [];
  const enriched = HALF_HOURS.map(t => slotsForDate.find(s => s.time === t) || { time: t, color: "white" });

  const paintSlot = time => {
    if (!currentColor || !selected) return;
    setSlots(prev => {
      const apply = (d, acc) => {
        const existing = (acc[d] || []).filter(s => s.time !== time);
        return { ...acc, [d]: currentColor === "white" ? existing : [...existing, { time, color: currentColor }] };
      };
      if (accelerator) {
        const weekday = new Date(activeDate).getDay();
        let result = { ...prev };
        const end = new Date(new Date().getFullYear(), 11, 31);
        const cur = new Date(activeDate);
        while (cur <= end) {
          if (cur.getDay() === weekday) result = apply(cur.toISOString().split("T")[0], result);
          cur.setDate(cur.getDate() + 1);
        }
        return result;
      }
      return apply(activeDate, prev);
    });
  };

  const saveSlots = async () => {
    if (!selected) return;
    setSaving(true);
    const rows = [];
    Object.entries(slots).forEach(([date, daySlots]) => {
      HALF_HOURS.forEach(time => {
        const s = daySlots.find(x => x.time === time);
        if (s && s.color !== "white") rows.push({ amenity_id: selected.id, date, time, status: s.color === "#32CD32" ? "available" : s.color === "#9747FF" ? "always_booked" : "booked" });
      });
    });
    await supabase.from("amenity_time_slots").delete().eq("amenity_id", selected.id);
    if (rows.length > 0) {
      const { error } = await supabase.from("amenity_time_slots").upsert(rows, { onConflict: "amenity_id,date,time" });
      if (error) { showToast("Save failed", "error"); setSaving(false); return; }
    }
    showToast("Time slots saved!", "success");
    setSaving(false);
  };

  const deleteAmenity = async id => {
    if (!window.confirm("Delete this facility? This cannot be undone.")) return;
    await supabase.from("amenities").delete().eq("id", id);
    setAmenities(p => p.filter(a => a.id !== id));
    if (selected?.id === id) { setSelected(null); setSlots({}); }
    showToast("Facility deleted", "info");
  };

  if (!show) return null;

  return (
    <>
      <Toast {...toast} C={C} />
      {showModal && (
        <AmenityModal show={showModal} userId={userId} session={session} sports={sports} editAmenity={editAmenity} C={C}
          onClose={() => { setShowModal(false); setEditAmenity(null); }}
          onSaved={() => { loadAmenities(); setShowModal(false); setEditAmenity(null); showToast(editAmenity ? "Facility updated!" : "Facility added!", "success"); }} />
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 18, paddingBottom: 80 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="accent-line" />
            <h2 className="section-head">Facilities</h2>
            <p className="section-sub">Manage your facilities and availability slots</p>
          </div>
          <button className="btn-primary" onClick={() => { setEditAmenity(null); setShowModal(true); }}>
            <Icon name="plus" size={15} color="#fff" /> Add Facility
          </button>
        </div>

        {loading
          ? <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spinner size={32} color={C.accent} /></div>
          : amenities.length === 0
            ? (
              <div style={{ textAlign: "center", padding: "60px 20px", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18 }}>
                <Icon name="trophy" size={48} color={C.textMuted} />
                <div className="section-head" style={{ fontSize: 17, marginTop: 14, marginBottom: 6 }}>No facilities yet</div>
                <p className="section-sub" style={{ marginBottom: 20 }}>Add your first facility to start accepting bookings</p>
                <button className="btn-primary" onClick={() => { setEditAmenity(null); setShowModal(true); }}>
                  <Icon name="plus" size={15} color="#fff" /> Add Facility
                </button>
              </div>
            )
            : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 12 }}>
                {amenities.map(a => {
                  const isSel = selected?.id === a.id;
                  return (
                    <div key={a.id} className="amenity-card card-lift"
                      style={{ cursor: "pointer", border: `1px solid ${isSel ? C.accent : C.border}`, background: isSel ? C.accentBg : C.bgCard }}
                      onClick={() => selectAmenity(a)}>
                      <div style={{ height: 120, overflow: "hidden", background: C.bgElevated, position: "relative" }}>
                        {a.image_url
                          ? <img src={a.image_url} alt={a.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Icon name="trophy" size={32} color={C.textMuted} />
                            </div>
                        }
                        {isSel && (
                          <div style={{ position: "absolute", top: 8, right: 8, width: 22, height: 22, borderRadius: "50%", background: C.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Icon name="check" size={13} color="#fff" />
                          </div>
                        )}
                      </div>
                      <div style={{ padding: 12 }}>
                        <div className="section-head" style={{ fontSize: 13, marginBottom: 3 }}>{a.name}</div>
                        <div style={{ fontSize: 11, color: C.textMuted }}>{a.sport_id} · €{a.price}/hr</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                          <button onClick={e => { e.stopPropagation(); setEditAmenity(a); setShowModal(true); }}
                            className="btn-ghost" style={{ padding: "5px 10px", fontSize: 11, flex: 1, justifyContent: "center", borderRadius: 8 }}>
                            <Icon name="edit" size={11} /> Edit
                          </button>
                          <button onClick={e => { e.stopPropagation(); deleteAmenity(a.id); }}
                            className="btn-danger" style={{ padding: "5px 10px", fontSize: 11, borderRadius: 8 }}>
                            <Icon name="trash" size={11} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
        }

        {/* Slot editor */}
        {selected && (
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
              <div>
                <div className="section-head" style={{ fontSize: 14 }}>{selected.name} — Availability</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                  {Object.values(slots).flat().filter(s => s.color !== "white").length} configured ·{" "}
                  {Object.values(slots).flat().filter(s => s.color === "#32CD32").length} available
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => setAccelerator(p => !p)}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 13px", borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: "pointer", border: `1px solid ${accelerator ? C.accent : C.accentBorder}`, background: accelerator ? C.accentBg : "transparent", color: accelerator ? C.accent : C.textMuted, transition: "all .15s" }}>
                  <Icon name="flash" size={13} color={accelerator ? C.accent : C.textMuted} />
                  Accelerator {accelerator ? "ON" : "OFF"}
                </button>
                <button className="btn-primary" onClick={saveSlots} disabled={saving} style={{ padding: "8px 16px", fontSize: 13 }}>
                  {saving ? <Spinner size={14} /> : <Icon name="save" size={14} color="#fff" />}
                  {saving ? "Saving…" : "Save Slots"}
                </button>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 14px" }}>
                <Icon name="calendar" size={14} color={C.accent} />
                <input type="date" value={viewDate}
                  onChange={e => { setViewDate(e.target.value); setViewDay(dayOfWeek(e.target.value)); }}
                  onKeyDown={stopKeys}
                  style={{ background: "transparent", border: "none", outline: "none", color: C.text, fontFamily: "DM Sans, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer" }} />
              </div>
              {accelerator && (
                <div className="badge badge-accent">⚡ All same weekdays this year</div>
              )}
            </div>

            <div style={{ display: "flex", gap: 5, overflowX: "auto", paddingBottom: 8, marginBottom: 14 }}>
              {DAYS.map(day => {
                const d = getDateForDay(day);
                const hasSl = (slots[d] || []).length > 0;
                return (
                  <button key={day} className={`tab-pill${viewDay === day ? " active" : ""}`}
                    onClick={() => setViewDay(day)} style={{ position: "relative", flexShrink: 0 }}>
                    {day.slice(0, 3)}
                    {hasSl && <span style={{ position: "absolute", top: 3, right: 4, width: 5, height: 5, borderRadius: "50%", background: viewDay === day ? C.accent : C.textMuted }} />}
                  </button>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 12 }}>
              {Object.entries(COLOR_META).map(([color, meta]) => (
                <button key={color} onClick={() => setCurrentColor(color)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 999,
                    fontSize: 11, fontWeight: 700, cursor: "pointer",
                    background: color === "white" ? C.bgElevated : color,
                    color: meta.text === "inherit" ? C.text : meta.text,
                    border: `2px solid ${currentColor === color ? C.accent : "transparent"}`,
                    transform: currentColor === color ? "scale(1.06)" : "scale(1)", transition: "all .15s",
                  }}>
                  {meta.label}
                </button>
              ))}
            </div>

            {currentColor && (
              <div style={{ fontSize: 11, color: C.textMuted, fontStyle: "italic", marginBottom: 10 }}>
                Painting: <strong style={{ color: C.text }}>{COLOR_META[currentColor]?.label}</strong>
                {accelerator ? " · All same weekdays" : " · Click slots to apply"}
              </div>
            )}

            <div className="slot-grid">
              {enriched.map(slot => (
                <button key={slot.time} className={`slot-btn${slot.color === "white" ? " unavail" : ""}`}
                  onClick={() => paintSlot(slot.time)}
                  style={slot.color === "white" ? {} : { background: slot.color, color: "#fff" }}>
                  {slot.time}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
              {[
                { label: "Clear Day", action: async () => { const ns = { ...slots }; delete ns[activeDate]; setSlots(ns); await supabase.from("amenity_time_slots").delete().eq("amenity_id", selected.id).eq("date", activeDate); showToast("Day cleared", "info"); } },
                { label: "Clear Month", action: async () => { if (!window.confirm("Clear all slots for this month?")) return; const m = activeDate.slice(0, 7); const ns = Object.fromEntries(Object.entries(slots).filter(([d]) => !d.startsWith(m))); setSlots(ns); await supabase.from("amenity_time_slots").delete().eq("amenity_id", selected.id).gte("date", `${m}-01`).lte("date", `${m}-31`); showToast("Month cleared", "info"); } },
                { label: "Clear Year", action: async () => { if (!window.confirm("Clear all slots for this year?")) return; const y = activeDate.slice(0, 4); const ns = Object.fromEntries(Object.entries(slots).filter(([d]) => !d.startsWith(y))); setSlots(ns); await supabase.from("amenity_time_slots").delete().eq("amenity_id", selected.id).gte("date", `${y}-01-01`).lte("date", `${y}-12-31`); showToast("Year cleared", "info"); } },
              ].map(({ label, action }) => (
                <button key={label} className="btn-danger" onClick={action} style={{ padding: "7px 13px", fontSize: 12, borderRadius: 8 }}>
                  <Icon name="trash" size={12} /> {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/* ─────────────────────────────────────────
   BOOKINGS TAB
───────────────────────────────────────── */
const BookingsTab = ({ userId, show, C }) => {
  const [toast, showToast] = useToast();
  const [upcoming, setUpcoming] = useState([]);
  const [past,     setPast]     = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [tab,      setTab]      = useState("upcoming");
  const [filter,   setFilter]   = useState("");
  const [modify,   setModify]   = useState(null);
  const [newTime,  setNewTime]  = useState("");
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data, error } = await supabase.from("bookings")
      .select("id,sport_id,date,duration,total_price,status,amenity_id,profile_id,amenities(id,name,image_url),profiles:profile_id(name,surname,phone_number)")
      .eq("institute_profile_id", userId);
    if (error) { showToast("Failed to load bookings", "error"); setLoading(false); return; }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const up = [], ps = [];
    (data || []).forEach(b => {
      const bd = new Date(b.date); bd.setHours(0, 0, 0, 0);
      const e = { ...b, name: b.amenities?.name || "", imageUrl: b.amenities?.image_url || "", price: b.total_price, customer: b.profiles ? `${b.profiles.name || ""} ${b.profiles.surname || ""}`.trim() || "Unknown" : "Unknown" };
      if (b.status === "cancelled" || bd < today) ps.push(e); else up.push(e);
    });
    setUpcoming(up); setPast(ps);
    setLoading(false);
  }, [userId]);

  useEffect(() => { if (show) load(); }, [show, load]);

  const displayed = (tab === "upcoming" ? upcoming : past).filter(b =>
    !filter || b.name.toLowerCase().includes(filter.toLowerCase()) || b.customer.toLowerCase().includes(filter.toLowerCase()) || b.date.includes(filter)
  );
  const revenue = upcoming.filter(b => b.status !== "cancelled").reduce((a, b) => a + Number(b.price || 0), 0);

  const cancelBooking = async id => {
    if (!window.confirm("Cancel this booking?")) return;
    const { error } = await supabase.from("bookings").update({ status: "cancelled" }).eq("id", id);
    if (error) { showToast("Failed to cancel", "error"); return; }
    const c = upcoming.find(b => b.id === id);
    if (c) { setUpcoming(p => p.filter(b => b.id !== id)); setPast(p => [{ ...c, status: "cancelled" }, ...p]); }
    showToast("Booking cancelled", "info");
  };

  const saveModify = async () => {
    const pat = /^([01]\d|2[0-3]):[0-5]\d to ([01]\d|2[0-3]):[0-5]\d$/;
    if (!pat.test(newTime)) { showToast("Use format HH:MM to HH:MM", "error"); return; }
    setSaving(true);
    const { error } = await supabase.from("bookings").update({ duration: newTime }).eq("id", modify.id);
    setSaving(false);
    if (error) { showToast("Update failed", "error"); return; }
    setUpcoming(p => p.map(b => b.id === modify.id ? { ...b, duration: newTime } : b));
    setModify(null); showToast("Booking updated!", "success");
  };

  if (!show) return null;

  return (
    <>
      <Toast {...toast} C={C} />
      {modify && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setModify(null); }}>
          <div className="modal-sheet">
            <h2 className="section-head" style={{ fontSize: 19, marginBottom: 4 }}>Modify Booking</h2>
            <p style={{ fontSize: 12, color: C.textMuted, marginBottom: 20 }}>{modify.name} · {modify.date}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 20 }}>
              <label className="field-label">New Time Slot</label>
              <input className="field-input" value={newTime} onChange={e => setNewTime(e.target.value)} onKeyDown={stopKeys} placeholder="e.g. 09:00 to 10:00" />
              <span style={{ fontSize: 11, color: C.textMuted }}>Format: HH:MM to HH:MM</span>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-ghost" onClick={() => setModify(null)} style={{ flex: 1, justifyContent: "center" }}>Cancel</button>
              <button className="btn-primary" onClick={saveModify} disabled={saving} style={{ flex: 1, justifyContent: "center" }}>
                {saving ? <Spinner size={14} /> : <Icon name="check" size={14} color="#fff" />}
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 80 }}>
        <div>
          <div className="accent-line" />
          <h2 className="section-head">Bookings</h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          {[
            { v: upcoming.length, l: "Upcoming" },
            { v: past.length, l: "Past" },
            { v: `€${revenue.toFixed(0)}`, l: "Revenue" },
          ].map(({ v, l }) => (
            <div key={l} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, textAlign: "center" }}>
              <div className="stat-num" style={{ fontSize: "clamp(18px,3.5vw,28px)" }}>{v}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: .5, marginTop: 4 }}>{l}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 5 }}>
            {["upcoming","past"].map(t => (
              <button key={t} className={`tab-pill${tab === t ? " active" : ""}`} onClick={() => setTab(t)}>
                {t[0].toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <input className="field-input" value={filter} onChange={e => setFilter(e.target.value)} onKeyDown={stopKeys}
            placeholder="Filter by name, date…" style={{ maxWidth: 220, padding: "8px 12px", fontSize: 12 }} />
        </div>

        {loading
          ? <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spinner size={32} color={C.accent} /></div>
          : displayed.length === 0
            ? (
              <div style={{ textAlign: "center", padding: "60px 20px", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18 }}>
                <Icon name="calendar" size={48} color={C.textMuted} />
                <div className="section-head" style={{ fontSize: 17, marginTop: 14 }}>No {tab} bookings</div>
              </div>
            )
            : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {displayed.map(b => (
                  <div key={b.id} className="booking-card" style={{ opacity: tab === "past" ? .75 : 1 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "100px 1fr" }}>
                      <div style={{ height: "100%", minHeight: 100, overflow: "hidden", background: C.bgElevated }}>
                        {b.imageUrl
                          ? <img src={b.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Icon name="trophy" size={26} color={C.textMuted} />
                            </div>
                        }
                      </div>
                      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 5 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span className="badge badge-accent" style={{ fontSize: 9 }}>{b.sport_id}</span>
                          {b.status === "cancelled" && <span className="badge badge-danger" style={{ fontSize: 9 }}>Cancelled</span>}
                        </div>
                        <div className="section-head" style={{ fontSize: 13 }}>{b.name}</div>
                        <div style={{ fontSize: 11, color: C.textSec }}>{b.customer}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <Icon name="calendar" size={11} color={C.accent} />
                          <span style={{ fontSize: 11, color: C.textSec }}>{b.date}</span>
                          <span style={{ fontSize: 11, color: C.accent, marginLeft: 4 }}>{b.duration}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                          <span className="stat-num" style={{ fontSize: 17 }}>{fmtPrice(b.price)}</span>
                          {tab === "upcoming" && b.status !== "cancelled" && (
                            <div style={{ display: "flex", gap: 6 }}>
                              <button className="btn-ghost" onClick={() => { setModify(b); setNewTime(b.duration); }} style={{ padding: "5px 10px", fontSize: 11, borderRadius: 8 }}>
                                <Icon name="edit" size={11} /> Modify
                              </button>
                              <button className="btn-danger" onClick={() => cancelBooking(b.id)} style={{ padding: "5px 9px", fontSize: 11, borderRadius: 8 }}>Cancel</button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
        }
      </div>
    </>
  );
};

/* ─────────────────────────────────────────
   AUTH SCREEN — Sign In + Sign Up
───────────────────────────────────────── */
const AuthScreen = ({ onAuth, C }) => {
  const navigate = useNavigate();
  const [email,    setEmail]      = useState("");
  const [password, setPassword]   = useState("");
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState("");
  const [showPw,   setShowPw]     = useState(false);

  const handleLogin = async e => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) throw err;
      onAuth(data.user);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: C.bg }}>
      {/* Header */}
      <header style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, height: 56, display: "flex", alignItems: "center", padding: "0 16px", background: C.headerBg, backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button aria-label="Back to venues" onClick={() => navigate("/venues")} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="arrowLeft" size={18} color={C.text} />
              </div>
            </button>
            <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 14, fontWeight: 700, color: C.text }}>
              Snows ProAm <span style={{ color: C.accent }}>Venues</span>
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              className="btn-ghost"
              aria-label="Home"
              onClick={() => navigate("/")}
              style={{ padding: "7px 14px", fontSize: 12, fontWeight: 500, color: C.text, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgElevated, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
            >
              <Icon name="home" size={13} />
              <span className="hide-mobile">Home</span>
            </button>
          </div>
        </div>
      </header>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "-20%", right: "-10%", width: "60vw", height: "60vw", background: `radial-gradient(ellipse, ${C.accentGlow} 0%, transparent 70%)` }} />
        <div style={{ position: "absolute", bottom: "-20%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(ellipse, ${C.accentGlow} 0%, transparent 70%)`, opacity: .5 }} />
      </div>
      <div style={{ width: "100%", maxWidth: 420, position: "relative", zIndex: 1, marginTop: 56 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: 18, background: C.accentBg, border: `1px solid ${C.accentBorder}`, marginBottom: 16 }}>
            <Icon name="trophy" size={32} color={C.accent} />
          </div>
          <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 900, color: C.text, marginBottom: 6, letterSpacing: -.5 }}>Sign in with your profile</h1>
          <p style={{ fontSize: 13, color: C.textSec }}>Venue tools are linked to an existing Snows ProAm profile.</p>
        </div>

        <form onSubmit={handleLogin}
          style={{ background: C.bgCard, border: `1px solid ${C.borderMd}`, borderRadius: 20, padding: 28, display: "flex", flexDirection: "column", gap: 14, boxShadow: C.shadowLg }}>

          {error && (
            <div style={{ padding: "10px 14px", borderRadius: 10, background: C.dangerBg, border: `1px solid ${C.dangerBorder}`, fontSize: 12, color: C.danger, lineHeight: 1.5 }}>{error}</div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label className="field-label">Email *</label>
            <input className="field-input" type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={stopKeys} placeholder="your@email.com" required autoComplete="email" />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label className="field-label">Password *</label>
            <div style={{ position: "relative" }}>
              <input className="field-input" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={stopKeys}
                placeholder="••••••••" required autoComplete="current-password" style={{ paddingRight: 44 }} />
              <button type="button" aria-label={showPw ? "Hide password" : "Show password"} onClick={() => setShowPw(p => !p)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: C.textMuted, display: "flex", padding: 0 }}>
                <Icon name={showPw ? "eyeOff" : "eye"} size={16} color={C.textMuted} />
              </button>
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={loading}
            style={{ justifyContent: "center", padding: "13px", marginTop: 4, fontSize: 15, borderRadius: 12 }}>
            {loading ? <Spinner size={18} /> : null}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <p style={{ textAlign: "center", marginTop: 16, fontSize: 12, color: C.textMuted }}>
          New venue? <button onClick={() => navigate("/venues/join")} style={{ background: "none", border: "none", color: C.accent, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>Start venue setup</button>
          {" · "}
          <a href="mailto:support@snowsproam.com" style={{ color: C.accent, fontWeight: 600, textDecoration: "none" }}>Contact us</a>
        </p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   ACCESS DENIED
───────────────────────────────────────── */
const AccessDenied = ({ onSignOut, C }) => (
  <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: C.bg }}>
    <div style={{ textAlign: "center", maxWidth: 360 }}>
      <div style={{ fontSize: 52, marginBottom: 16 }}>🔒</div>
      <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 26, fontWeight: 900, color: C.text, marginBottom: 10 }}>Business Access Only</h2>
      <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.65, marginBottom: 24 }}>This portal is for verified Snows ProAm venues. Your account doesn't have venue access.</p>
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button className="btn-ghost" onClick={onSignOut}><Icon name="logout" size={14} /> Sign Out</button>
        <a href="mailto:support@snowsproam.com" className="btn-primary" style={{ textDecoration: "none" }}><Icon name="mail" size={14} color="#fff" /> Contact Us</a>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────
   MAIN PARTNER PORTAL
───────────────────────────────────────── */
export default function PartnerPortal() {
  const [C, toggleTheme, isDark] = useTheme();
  const navigate = useNavigate();
  useEffect(() => { injectCSS(C); }, [C]);

  const [user,        setUser]        = useState(null);
  const [session,     setSession]     = useState(null);
  const [profile,     setProfile]     = useState(null);
  const [amenities,   setAmenities]   = useState([]);
  const [gallery,     setGallery]     = useState([]);
  const [checking,    setChecking]    = useState(true);
  const [tab,         setTab]         = useState("profile");
  const [publicSlug,  setPublicSlug]  = useState(null);
  const [publicProfile, setPublicProfile] = useState(null);
  const [publicProfileLoading, setPublicProfileLoading] = useState(true);
  const [publicProfileError, setPublicProfileError] = useState(false);
  const [publicProfileAttempt, setPublicProfileAttempt] = useState(0);

  useEffect(() => {
    const slug = new URLSearchParams(window.location.search).get("p");
    if (slug) setPublicSlug(slug);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session) { setUser(data.session.user); setSession(data.session); }
      else setChecking(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
      if (event === "SIGNED_OUT") { setUser(null); setSession(null); setProfile(null); setAmenities([]); setGallery([]); setChecking(false); }
      else if (sess?.user) { setUser(sess.user); setSession(sess); }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setChecking(true);
      const { data } = await supabase.from("profiles").select("*").eq("id", user.id).maybeSingle();
      setProfile(data); setGallery(data?.gallery_imgs || []);
      setChecking(false);
    })();
  }, [user]);

  const loadAmenities = useCallback(async ownerId => {
    const { data } = await supabase.from("amenities").select("*").eq("business_id", ownerId);
    setAmenities(data || []);
  }, []);

  useEffect(() => { if (user?.id) loadAmenities(user.id); }, [user, loadAmenities]);

  const [publicProfileRating, setPublicProfileRating] = useState(null);
  const [publicProfileReviews, setPublicProfileReviews] = useState([]);
  const [profileRating, setProfileRating] = useState(null);

  useEffect(() => {
    if (!publicSlug) return;
    let active = true;
    setPublicProfileLoading(true);
    setPublicProfileError(false);
    setPublicProfile(null);
    setPublicProfileRating(null);
    setPublicProfileReviews([]);
    supabase.from("profiles").select("*").eq("slug", publicSlug).maybeSingle().then(({ data, error }) => {
      if (!active) return;
      if (error) throw error;
      setPublicProfile(data);
      if (data?.id) {
        loadAmenities(data.id);
        // Fetch ratings for this profile
        supabase.from("partner_ratings").select("*, profiles(name, profile_img)").eq("partner_id", data.id).order("created_at", { ascending: false }).then(({ data: ratingsData }) => {
          if (ratingsData && ratingsData.length > 0) {
            const sum = ratingsData.reduce((acc, r) => acc + r.rating, 0);
            setPublicProfileRating({
              averageRating: sum / ratingsData.length,
              reviewCount: ratingsData.length,
            });
            setPublicProfileReviews(ratingsData);
          } else {
            setPublicProfileRating({ averageRating: 0, reviewCount: 0 });
            setPublicProfileReviews([]);
          }
        });
      }
    }).catch(() => {
      if (active) setPublicProfileError(true);
    }).finally(() => {
      if (active) setPublicProfileLoading(false);
    });
    return () => { active = false; };
  }, [publicSlug, loadAmenities, publicProfileAttempt]);

  // Fetch ratings for logged-in user's profile
  useEffect(() => {
    if (!user?.id) return;
    supabase.from("partner_ratings").select("*").eq("partner_id", user.id).then(({ data: ratingsData }) => {
      if (ratingsData && ratingsData.length > 0) {
        const sum = ratingsData.reduce((acc, r) => acc + r.rating, 0);
        setProfileRating({
          averageRating: sum / ratingsData.length,
          reviewCount: ratingsData.length,
        });
      } else {
        setProfileRating({ averageRating: 0, reviewCount: 0 });
      }
    });
  }, [user]);

  const signOut = async () => { await supabase.auth.signOut({ scope: "global" }); };

  const reloadProfile = useCallback(() => {
    if (!user) return;
    supabase.from("profiles").select("*").eq("id", user.id).maybeSingle().then(({ data }) => {
      setProfile(data); setGallery(data?.gallery_imgs || []);
    });
    loadAmenities(user.id);
  }, [user, loadAmenities]);

  const isBusiness = profile?.account_type === "institute" || profile?.is_business === true ||
    profile?.type === "institute" || profile?.role === "institute" || profile?.role === "partner";

  // ── Public profile view ──
  if (publicSlug) {
    const displayProfile = publicProfile;
    const displayGallery = displayProfile?.gallery_imgs || [];
    return (
      <div style={{ minHeight: "100vh", background: C.bg, color: C.text }}>
        <header className="venue-public-header" style={{ position: "sticky", top: 0, zIndex: 100, height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", background: C.headerBg, backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderBottom: `1px solid ${C.border}` }}>
          <button onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <img src={logo} alt="Snows ProAm" style={{ width: 18, height: 18, objectFit: "contain" }} />
            </div>
            <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>
              Snows ProAm <span style={{ color: C.accent }}>Venues</span>
            </span>
          </button>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="theme-btn" aria-label={isDark ? "Use light theme" : "Use dark theme"} onClick={toggleTheme}>
              <Icon name={isDark ? "sun" : "moon"} size={14} color={C.textSec} />
            </button>
            {user && isBusiness && (
              <button className="tab-pill active" onClick={() => setPublicSlug(null)} style={{ fontSize: 12 }}>← Dashboard</button>
            )}
            {!user && (
              <button className="btn-ghost" onClick={() => setPublicSlug(null)} style={{ fontSize: 12, padding: "7px 14px" }}>Login</button>
            )}
          </div>
        </header>
        {displayProfile
          ? <PublicProfile profile={displayProfile} amenities={amenities} gallery={displayGallery} C={C} ratingStats={publicProfileRating} reviews={publicProfileReviews} />
          : <main style={{ display: "grid", justifyItems: "center", alignContent: "center", gap: 16, minHeight: "60vh", padding: 24, textAlign: "center" }}>
              {publicProfileLoading ? <div role="status"><Spinner size={36} color={C.accent} /><p>Loading venue…</p></div> : <>
                <div role={publicProfileError ? "alert" : "status"}>
                  <h1 style={{ fontSize: 24 }}>{publicProfileError ? "Couldn’t load this venue" : "Venue unavailable"}</h1>
                  <p>{publicProfileError ? "Check your connection and try again." : "This venue’s public page isn’t available. Browse the directory to find another venue."}</p>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 12 }}>
                  {publicProfileError && <button className="btn-primary" onClick={() => setPublicProfileAttempt(attempt => attempt + 1)}>Try again</button>}
                  <button className="btn-ghost" onClick={() => navigate("/venues")}>Back to venues</button>
                </div>
              </>}
            </main>
        }
      </div>
    );
  }

  // ── Loading ──
  if (checking) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14 }}>
      <Spinner size={36} color={C.accent} />
      <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 600, fontFamily: "'Sora',sans-serif", textTransform: "uppercase", letterSpacing: 1 }}>Loading…</span>
    </div>
  );

  if (!user) return <AuthScreen onAuth={u => setUser(u)} C={C} />;
  if (!isBusiness && profile) return <AccessDenied onSignOut={signOut} C={C} />;

  const navTabs = [
    { id: "profile",   label: "My Page",    icon: "globe" },
    { id: "dashboard", label: "Facilities", icon: "trophy" },
    { id: "bookings",  label: "Bookings",   icon: "calendar" },
    { id: "analytics", label: "Analytics",  icon: "chart" },
    { id: "settings",  label: "Settings",   icon: "settings" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text }}>
      {/* Header */}
      <header className="venue-public-header" style={{ position: "sticky", top: 0, zIndex: 100, height: 56, display: "flex", alignItems: "center", gap: 12, padding: "0 16px", background: C.headerBg, backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          <div style={{ width: 3, height: 28, background: C.accentGrad, borderRadius: 2, flexShrink: 0 }} />
          {profile?.profile_img && (
            <img src={profile.profile_img} alt="" style={{ width: 30, height: 30, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
          )}
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: .3 }}>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 900, color: C.text }}>
              {profile?.name || "Venue Portal"}
            </div>
            {profile?.institute_type && (
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 600, color: C.accentLight, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {profile.institute_type}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              className="btn-ghost"
              aria-label="Home"
              onClick={() => navigate("/")}
              style={{ padding: "7px 12px", fontSize: 12 }}
            >
              <Icon name="home" size={13} /> <span className="hide-mobile">Home</span>
            </button>
            <button
              className="btn-ghost"
              onClick={() => navigate("/venues")}
              aria-label="Browse venues"
              style={{ padding: "7px 12px", fontSize: 12 }}
            >
              <Icon name="globe" size={13} /> <span className="hide-mobile">Browse Venues</span>
            </button>
            <button className="theme-btn" aria-label={isDark ? "Use light theme" : "Use dark theme"} onClick={toggleTheme} title={isDark ? "Light mode" : "Dark mode"}>
              <Icon name={isDark ? "sun" : "moon"} size={14} color={C.textSec} />
            </button>
            {profile?.slug && (
              <button className="btn-ghost" aria-label="My page" onClick={() => setPublicSlug(profile.slug)} style={{ padding: "7px 12px", fontSize: 12 }}>
                <Icon name="globe" size={13} /> <span className="hide-mobile">My Page</span>
              </button>
            )}
            <button className="btn-ghost" aria-label="Sign out" onClick={signOut} style={{ padding: "7px 12px", fontSize: 12 }}>
              <Icon name="logout" size={13} /> <span className="hide-mobile">Sign Out</span>
            </button>
          </div>
      </header>

      {/* Hero bar with cover */}
      <div style={{ background: C.bgCard, borderBottom: `1px solid ${C.border}`, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, right: 0, width: 300, height: "100%", background: `linear-gradient(120deg, transparent 30%, ${C.accentGlow})`, pointerEvents: "none" }} />
        {profile?.cover_img && (
          <img src={profile.cover_img} alt="cover" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: .07 }} />
        )}
        <div style={{ position: "relative", maxWidth: 980, margin: "0 auto", padding: "18px 16px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ width: 50, height: 50, borderRadius: 13, overflow: "hidden", border: `2px solid ${C.accentBorder}`, background: C.bgElevated, flexShrink: 0 }}>
              {profile?.profile_img
                ? <img src={profile.profile_img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon name="institute" size={24} color={C.accent} />
                  </div>
              }
            </div>
            <div>
              <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(18px,3.5vw,26px)", fontWeight: 900, color: C.text, letterSpacing: -.3 }}>{profile?.name || "My Business"}</h1>
              {profile?.address && (
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon name="location" size={11} color={C.textMuted} />{profile.address}
                </div>
              )}
            </div>
            <div className="badge badge-accent" style={{ marginLeft: "auto" }}>✓ VERIFIED PARTNER</div>
          </div>

          {/* Nav */}
          <div style={{ display: "flex", gap: 3, overflowX: "auto", paddingBottom: 0 }}>
            {navTabs.map(t => (
              <button key={t.id} className={`tab-pill${tab === t.id ? " active" : ""}`}
                onClick={() => setTab(t.id)} style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0, borderRadius: "9px 9px 0 0" }}>
                <Icon name={t.icon} size={12} color={tab === t.id ? C.accent : C.textMuted} />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main */}
      <main style={{ maxWidth: 980, margin: "0 auto", padding: "24px 16px", minHeight: "60vh" }}>

        {/* MY PAGE TAB */}
        {tab === "profile" && (
          <div>
            <div style={{ background: C.bgCard, border: `1px solid ${C.accentBorder}`, borderRadius: 14, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
              <Icon name="info" size={15} color={C.accent} />
              <span style={{ fontSize: 12, fontWeight: 600, color: C.textSec }}>This is your public-facing page.</span>
              <button onClick={() => setTab("settings")} style={{ background: "none", border: "none", color: C.accent, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0 }}>Edit in Settings →</button>
            </div>
            {profile?.slug
              ? <div style={{ marginBottom: 20 }}><QRSection slug={profile.slug} C={C} /></div>
              : (
                <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 18px", marginBottom: 20, fontSize: 13, color: C.textSec }}>
                  💡 Save your profile in Settings to generate your public page URL and QR code.
                </div>
              )
            }
            <PublicProfile profile={profile} amenities={amenities} gallery={gallery} C={C} ratingStats={profileRating} />
          </div>
        )}

        <DashboardTab userId={user?.id} session={session} show={tab === "dashboard"} C={C} />
        <BookingsTab  userId={user?.id} show={tab === "bookings"} C={C} />
        <AnalyticsTab userId={user?.id} show={tab === "analytics"} C={C} />
        <SettingsTab  userId={user?.id} profile={profile} session={session} show={tab === "settings"} onSaved={reloadProfile} C={C} />
      </main>

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${C.border}`, padding: "18px 16px", textAlign: "center" }}>
        <div style={{ fontSize: 11, color: C.textMuted, fontFamily: "'Sora',sans-serif", textTransform: "uppercase", letterSpacing: .5 }}>
          Powered by <span style={{ background: C.accentGrad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", fontWeight: 700 }}>Snows ProAm</span>
          {" · "}
          <a href="mailto:support@snowsproam.com" style={{ color: C.accent, fontWeight: 600, textDecoration: "none" }}>support@snowsproam.com</a>
        </div>
      </footer>
    </div>
  );
}
